// @ts-strict-ignore
import * as React from 'react';
import * as AnalyticEvents from '../../utils/analytics';
import { useDispatch, useSelector } from 'react-redux';
import { dayjs } from '@/extendedDayjs';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import Table from 'antd/lib/table';
import Popconfirm from 'antd/lib/popconfirm';
import Spin from 'antd/lib/spin';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { RootState } from '../../reducers/reducers';
import {
    selectAllAppGroupNamesInOrg,
    selectAllAppsIdsInOrg,
    selectAuthCredentials,
    selectOrg,
    selectUserAppDataByAppIdMap,
} from '../../selectors/selectors';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import styles from '../styles/settingsStyles';
import { Key, PublicAPIKey, SubscriptionFeatureType } from '../../types';
import { OrgComponentProps } from '../../typescriptHelpers';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { DatetimeFormat } from '../../timeHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { UserPermissionScope } from '@/utils/hooks/useUserCanPerformAction';
import AddApiKeyModal from './AddApiKeyModal';
import PublicApiRequestLog from './PublicApiRequestLog';
import { tryUpdateOrgSettings } from '../../actions/orgSettings';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { allReleasePropertiesFrom } from '../../helpers/trackingHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Tag from 'antd/lib/tag';
import { displayTextForUserGroup, getDefaultUserGroupDisplayText } from '../../helpers/displayHelpers';
import {
    appNameWithPlatform,
    AppNameWithSecondaryPlatform,
    platformLabelForAppName,
} from '../../helpers/mdmPlatformOverride';
import Text from 'antd/lib/typography/Text';
import { getAvailableAppsFromAppDataMap } from '../../helpers';
import Tooltip from 'antd/lib/tooltip';
import { APIKeyUsageStats, getApiKeyUsage } from '../../api';

const ApiSettings: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const { orgId } = props;
    const dispatch = useDispatch();

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const appsInOrg = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, orgId));
    const appGroupsInOrg = useSelector((state: RootState) => selectAllAppGroupNamesInOrg(state, orgId));
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));

    const keys = useSelector((state: RootState) => {
        const org = selectOrg(state, orgId);
        const agentKeyIds = new Set((org.agents ?? []).map((agent) => agent.apiKeyId));
        return (org.apiKeys ?? [])
            .filter((key) => !agentKeyIds.has(key.id))
            .sort((a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix());
    });
    const keyNames = keys.map((key) => key.name);

    const [displayAddApiModal, setDisplayAddApiModal] = React.useState(false);
    const [deletePopoverVisible, setDeletePopoverVisible] = React.useState({});
    const [usageByKeyId, setUsageByKeyId] = React.useState<Record<string, APIKeyUsageStats>>({});
    const [usageLoading, setUsageLoading] = React.useState(false);

    const appDataByAppIdMap = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, props.orgId));
    const allAvailableApps = Object.values(appDataByAppIdMap);

    const fetchApiKeyUsage = React.useCallback(() => {
        setUsageLoading(true);
        getApiKeyUsage(orgId, auth)
            .then((stats: APIKeyUsageStats[]) => {
                const map: Record<string, APIKeyUsageStats> = {};
                if (stats) for (const stat of stats) map[stat.keyId] = stat;
                setUsageByKeyId(map);
            })
            .catch(() => setUsageByKeyId({}))
            .finally(() => setUsageLoading(false));
    }, [orgId, auth]);

    React.useEffect(() => fetchApiKeyUsage(), []);

    const showAddApiModal = () => {
        setDisplayAddApiModal(true);
    };

    const deletePublicApiKey = (apiKeyId: string) => {
        dispatch(
            tryUpdateOrgSettings(
                OrgSettingsUpdatable.DeletePublicApiKeyEndpoint,
                {
                    orgId,
                    apiKeyId,
                },
                apiKeyId,
            ),
        );
        const properties = allReleasePropertiesFrom(org, null, null, appsInOrg, appGroupsInOrg);
        AnalyticEvents.track(AnalyticEvents.Event.DeletePublicApiKey, properties);
    };

    const renderTitle = (title: string) => {
        return <Typography.Text strong>{title}</Typography.Text>;
    };

    const renderAction = (key: Key) => {
        const popoverVisibleFromState = deletePopoverVisible[key.id];
        const setPopoverVisible = (visible: boolean, id: string) => {
            const updatedPopoverVisible = {
                ...deletePopoverVisible,
                [id]: visible,
            };

            setDeletePopoverVisible(updatedPopoverVisible);
        };
        return (
            <RemoveKeyAction
                keyId={key.id}
                isDisabled={false}
                popoverVisible={popoverVisibleFromState}
                setPopoverVisible={setPopoverVisible}
                onDelete={deletePublicApiKey}
            />
        );
    };

    const renderUsage = (_: any, key: PublicAPIKey) => {
        if (usageLoading) {
            return <Spin size="small" />;
        }

        const usage = usageByKeyId[key.id];
        if (!usage) {
            return <Text type="secondary">--</Text>;
        }

        const displayText = `${usage.used.toLocaleString()} / ${usage.limit.toLocaleString()}`;

        return <Text>{displayText}</Text>;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
            <Typography.Text>Add and remove API keys that can be used to access the Runway API.</Typography.Text>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Space size={8}>
                    <Typography.Text style={styles.appSettingsSectionTitleStyle}>API Keys</Typography.Text>
                </Space>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.PublicAPI} requiresTooltip>
                    {(isDisabled) => (
                        <PermissionedActionButton
                            disabled={isDisabled}
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            permissionScope={UserPermissionScope.Org}
                            icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                            onClick={showAddApiModal}
                        >
                            Create new key
                        </PermissionedActionButton>
                    )}
                </PlanRestrictedFeatureWrapper>
            </div>
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.PublicAPI} shouldFakeDisabledState>
                <Table
                    dataSource={keys}
                    columns={[
                        {
                            title: renderTitle('API key name'),
                            dataIndex: 'name',
                            key: 'name',
                            width: 200,
                            ellipsis: true,
                        },
                        {
                            title: renderTitle('Created by'),
                            dataIndex: 'createdBy',
                            key: 'createdBy',
                            width: 160,
                            ellipsis: true,
                        },
                        {
                            title: renderTitle('Created at'),
                            dataIndex: 'createdAt',
                            width: 190,
                            render: (date: string) => {
                                return dayjs(date).format(DatetimeFormat.DayAbbreviatedMonthDateYearTimeAbbreviated);
                            },
                        },
                        {
                            title: renderTitle('Last used'),
                            dataIndex: 'lastUsedAt',
                            width: 190,
                            render: (date: string) => {
                                return date ?
                                        dayjs(date).format(DatetimeFormat.DayAbbreviatedMonthDateYearTimeAbbreviated)
                                    :   <Typography.Text style={{ color: antGrey.grey7 }}>Unused</Typography.Text>;
                            },
                        },
                        {
                            title: renderTitle('Daily usage'),
                            key: 'usage',
                            width: 130,
                            render: renderUsage,
                        },
                        {
                            title: renderTitle('User groups'),
                            dataIndex: 'groups',
                            width: 200,
                            ellipsis: true,
                            render: (_: any, key: PublicAPIKey) => {
                                if (key.isAdmin) {
                                    return <Text type="secondary">All groups</Text>;
                                }

                                if (!key.groups || key.groups.length === 0) {
                                    return <Text type="secondary">No groups</Text>;
                                }
                                const allGroups = key.groups
                                    .map((groupId) => {
                                        const group = org.orgSettings.groups[groupId];
                                        if (group) {
                                            return displayTextForUserGroup(group);
                                        }
                                        return getDefaultUserGroupDisplayText(groupId);
                                    })
                                    .join(', ');

                                return <Text>{allGroups}</Text>;
                            },
                        },
                        {
                            title: renderTitle('Apps'),
                            width: 200,
                            dataIndex: 'appIds',
                            render: (_: any, key: PublicAPIKey) => {
                                if (key.isAdmin) {
                                    return <Text type="secondary">All apps</Text>;
                                }

                                const availableApps = getAvailableAppsFromAppDataMap({
                                    appDataMap: appDataByAppIdMap,
                                    appIds: key.appIds,
                                });

                                if (!availableApps || availableApps.length === 0) {
                                    return <Text type="secondary">All apps</Text>;
                                }

                                const appsToDisplay = availableApps.slice(0, 3);
                                const remainingApps = availableApps.slice(3);
                                const remainingAppsCountText = remainingApps.length == 1 ? 'other' : 'others';

                                return (
                                    <>
                                        {appsToDisplay.map((appData, index) => {
                                            return (
                                                <span key={appData.appId}>
                                                    <AppNameWithSecondaryPlatform
                                                        appName={appData.appName}
                                                        platformLabel={platformLabelForAppName(appData)}
                                                    />
                                                    {index !== appsToDisplay.length - 1 && ', '}
                                                </span>
                                            );
                                        })}
                                        {remainingApps.length > 0 && (
                                            <Tooltip
                                                title={
                                                    <div>
                                                        {remainingApps.map((appData, _) => (
                                                            <div key={appData.appId}>
                                                                {appNameWithPlatform(
                                                                    appData.appName,
                                                                    platformLabelForAppName(appData),
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                }
                                            >
                                                <span>
                                                    , and {remainingApps.length} {remainingAppsCountText}
                                                </span>
                                            </Tooltip>
                                        )}
                                    </>
                                );
                            },
                        },
                        {
                            title: renderTitle('Permissions'),
                            dataIndex: 'isAdmin',
                            width: 120,
                            render: (isAdmin: boolean) => {
                                if (isAdmin) {
                                    return <Tag style={{ ...tagStyle, color: runwayColors.primary }}>Admin</Tag>;
                                } else {
                                    return <></>;
                                }
                            },
                        },
                        {
                            title: '',
                            align: 'right',
                            render: renderAction,
                        },
                    ]}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                    style={{ marginTop: 24, marginBottom: 24 }}
                />
            </PlanRestrictedFeatureWrapper>
            <PublicApiRequestLog orgId={orgId} />
            <AddApiKeyModal
                allKeyNames={keyNames}
                orgId={orgId}
                onClose={() => setDisplayAddApiModal(false)}
                open={displayAddApiModal}
                allAvailableApps={allAvailableApps}
            />
        </div>
    );
};

interface RemoveKeyActionProps {
    isDisabled: boolean;
    onDelete: (string) => void;
    keyId: string;
    popoverVisible: string;
    setPopoverVisible: (boolean, string) => void;
}

const RemoveKeyAction: React.FC<RemoveKeyActionProps> = (props: RemoveKeyActionProps) => {
    const { isDisabled, onDelete, keyId, popoverVisible, setPopoverVisible } = props;
    const deleteApiKeyIsLoading = useIsLoading(OrgSettingsUpdatable.DeletePublicApiKeyEndpoint, keyId);
    const deleteApiKeySuccess = useUpdated(OrgSettingsUpdatable.DeletePublicApiKeyEndpoint, keyId);
    const deleteApiKeyError = useError(OrgSettingsUpdatable.DeletePublicApiKeyEndpoint, keyId);

    const popoverVisibleFromState = popoverVisible;
    const popoverIsVisible = deleteApiKeyIsLoading || !!popoverVisibleFromState;

    React.useEffect(() => {
        if (deleteApiKeySuccess) {
            globalMessage.success('Api key deleted!');
        }
    }, [deleteApiKeySuccess]);

    React.useEffect(() => {
        if (deleteApiKeyError) {
            globalMessage.error(deleteApiKeyError);
        }
    }, [deleteApiKeyError]);

    return (
        <Popconfirm
            title={"Are you sure you want to delete this API key? The key's access will also be revoked."}
            placement="topRight"
            open={popoverIsVisible}
            onConfirm={() => onDelete(keyId)}
            okText="Yes, delete and revoke access"
            onCancel={() => setPopoverVisible(false, keyId)}
            cancelText="No"
            okButtonProps={{ loading: deleteApiKeyIsLoading }}
            overlayStyle={{ whiteSpace: 'pre-line' }}
        >
            <PermissionedActionButton
                danger
                type="text"
                disabled={isDisabled}
                actionType={RunwayUserAction.UAUpdateAppSettings}
                permissionScope={UserPermissionScope.Org}
                icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                onClick={() => {
                    setPopoverVisible(!popoverVisible, keyId);
                }}
            >
                Delete
            </PermissionedActionButton>
        </Popconfirm>
    );
};

export default ApiSettings;
